import { Instagram } from "lucide-react";
import { cn } from "@/utils/helpers";
import PlatformGlyph from "../../components/PlatformGlyph";
import { posterFrameUrl } from "../videoThumbnails";
import type { LiveFile } from "./liveTypes";

/**
 * A platform's mark on Live: the real TikTok and Pinterest glyphs, lucide's
 * Instagram (the approved frames draw it that way), the shared glyph for the
 * rest. The name rides in an sr-only span at the call site.
 */
export function PlatformMark({ platform, className }: { platform: string; className?: string }) {
  if (platform === "INSTAGRAM") return <Instagram className={cn("size-4", className)} strokeWidth={2} aria-hidden="true" />;
  return <PlatformGlyph platform={platform} className={className} />;
}

/** The platform chip on a post's picture: media glass, a logo, never a word. */
export function MediaMark({ platform, label, className }: { platform: string; label: string; className?: string }) {
  return (
    <span className={cn("co-media-chip", className)}>
      <PlatformMark platform={platform} className="size-[13px]" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Tag / Platform: the platform's mark on a 24px tonal tile, radius 6, in lists (the brief's ask, the kit). */
export function MarkWell({ platform, label }: { platform: string; label: string }) {
  return (
    <span className="grid size-6 shrink-0 place-items-center rounded-tag bg-[var(--co-tonal)] text-co-muted">
      <PlatformMark platform={platform} className="size-3.5" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** A file's picture: the still where there is one, else a frame from the video. */
export function FilePicture({ file, alt = "", className }: { file: LiveFile | null | undefined; alt?: string; className?: string }) {
  if (!file) return null;
  if (file.thumbUrl) return <img src={file.thumbUrl} alt={alt} className={className} loading="lazy" draggable={false} />;
  if (file.kind === "VIDEO" && file.mediaUrl) {
    return <video src={posterFrameUrl(file.mediaUrl)} preload="metadata" muted playsInline aria-label={alt || undefined} className={className} />;
  }
  return null;
}

/** "{a}, {b} and {c}". */
export function listWords(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Pixel width for a post at the brief's ratio, so a row of posts shares one height where it can. */
export function postWidth(ratio: string, size: "desk" | "tablet" | "phone" | "results"): number {
  const base = size === "desk" ? 280 : size === "tablet" ? 230 : 240;
  if (ratio === "9:16") return base;
  if (ratio === "2:3") return Math.round(base * 0.86);
  if (ratio === "4:5" || ratio === "1:1") return Math.round(base * 0.9);
  return Math.round(base * 1.3);
}
